import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Navbar, Button, Nav, Dropdown, Badge } from 'react-bootstrap';
import { useAuth } from '../../contexts/AuthContext';
import { fetchUserNotifications, markNotificationAsRead } from '../../services/api';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import styles from './TopNavbar.module.css';

const TopNavbar = ({ toggleSidebar }) => {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const handleClickNotification = async (notif) => {
        const codigo = notif.codigo_produto;
        const origem = notif.origem_produto || 'ecommerce';

        try {
            markNotificationAsRead(notif.id).then(() => {
                queryClient.invalidateQueries(['user-notifications']);
            });
        } catch (error) {
            console.error(error);
        }

        // Determinar rota baseada na origem
        if (origem === 'catalogo_pedido') {
            // Pedido novo do catálogo da Centro → Catálogo Cliente, aba Pedidos
            navigate('/catalogo-cliente?aba=pedidos');
        } else if (origem.includes('conferencia_limeira')) {
            // Notificação de conferência de pedidos - ir para a página de conferência
            navigate('/limeira/conferencia');
        } else if (origem.includes('ecommerce')) {
            navigate(`/ecommerce?search=${codigo}&action=open_obs`);
        } else if (origem.includes('caxias') || origem.includes('004')) {
            navigate(`/loja-fisica/caxias?search=${codigo}&action=open_obs`);
        } else if (origem.includes('centro') || origem.includes('001')) {
            navigate(`/loja-fisica/centro?search=${codigo}&action=open_obs`);
        } else {
            // Default: E-commerce
            navigate(`/ecommerce?search=${codigo}&action=open_obs`);
        }
    };

    // Buscar notificações
    const { data: notificationsData } = useQuery({
        queryKey: ['user-notifications'],
        queryFn: fetchUserNotifications,
        refetchInterval: 30000, // Atualizar a cada 30 segundos
        staleTime: 10000
    });

    const notificationsCount = notificationsData?.count || 0;
    const notifications = notificationsData?.data || [];

    return (
        <Navbar expand="lg" className={`shadow-sm ${styles.navbar}`} style={{ backgroundColor: '#0f172a', borderBottom: '1px solid #1e293b' }}>
            <div className="d-flex align-items-center w-100">
                <Button
                    variant="link"
                    className="me-3 p-0"
                    onClick={toggleSidebar}
                    style={{ color: '#94a3b8' }}
                >
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" viewBox="0 0 16 16">
                        <path fillRule="evenodd" d="M2.5 12a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5zm0-4a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5zm0-4a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5z" />
                    </svg>
                </Button>

                <Navbar.Brand href="/" className="fw-bold d-none d-lg-block" style={{ color: '#f8fafc' }}>

                </Navbar.Brand>

                <Nav className="ms-auto align-items-center">
                    <Dropdown align="end" className="me-3">
                        <Dropdown.Toggle variant="link" id="dropdown-notifications" className="position-relative p-0 border-0" style={{ color: '#94a3b8' }}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
                                <path d="M8 16a2 2 0 0 0 2-2H6a2 2 0 0 0 2 2zM8 1.918l-.797.161A4.002 4.002 0 0 0 4 6c0 .628-.134 2.197-.459 3.742-.16.767-.376 1.566-.663 2.258h10.244c-.287-.692-.502-1.49-.663-2.258C12.134 8.197 12 6.628 12 6a4.002 4.002 0 0 0-3.203-3.92L8 1.917zM14.22 12c.223.447.481.801.78 1H1c.299-.199.557-.553.78-1C2.68 10.2 3 6.88 3 6c0-2.42 1.72-4.44 4.005-4.901a1 1 0 1 1 1.99 0A5.002 5.002 0 0 1 13 6c0 .88.32 4.2 1.22 6z" />
                            </svg>
                            {notificationsCount > 0 && (
                                <Badge bg="danger" pill className="position-absolute top-0 start-100 translate-middle" style={{ fontSize: '0.6rem' }}>
                                    {notificationsCount > 99 ? '99+' : notificationsCount}
                                </Badge>
                            )}
                        </Dropdown.Toggle>

                        <Dropdown.Menu style={{ minWidth: '300px', maxHeight: '400px', overflowY: 'auto' }}>
                            <Dropdown.Header>Notificações ({notificationsCount})</Dropdown.Header>
                            <Dropdown.Divider />
                            {notifications.length === 0 ? (
                                <Dropdown.Item className="text-muted text-center py-3">
                                    Nenhuma notificação nova
                                </Dropdown.Item>
                            ) : (
                                notifications.slice(0, 5).map(notif => {
                                    const origem = notif.origem_produto || 'ecommerce';
                                    let icon = '🛒';
                                    let label = 'Produto';
                                    if (origem === 'catalogo_pedido') {
                                        icon = '🛍️';
                                        label = 'Pedido do catálogo';
                                    } else if (origem.includes('conferencia_limeira')) {
                                        icon = '📦';
                                        label = 'Pedido';
                                    } else if (origem.includes('caxias') || origem.includes('centro') || origem.includes('001') || origem.includes('004')) {
                                        icon = '🏬';
                                    }

                                    return (
                                        <Dropdown.Item
                                            key={notif.id}
                                            className="py-2 border-bottom"
                                            onClick={() => handleClickNotification(notif)}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            <div className="d-flex">
                                                <span style={{ fontSize: '1.2rem', marginRight: '8px' }}>{icon}</span>
                                                <div className="d-flex flex-column flex-grow-1">
                                                    <span className="fw-bold small">{label} #{notif.codigo_produto}</span>
                                                    <span className="small text-muted text-truncate" style={{ maxWidth: '220px' }}>
                                                        {notif.observacao}
                                                    </span>
                                                    <div className="d-flex justify-content-between mt-1">
                                                        <span style={{ fontSize: '0.7rem' }} className="text-primary">De: {notif.criado_por || 'Sistema'}</span>
                                                        <span style={{ fontSize: '0.7rem' }} className="text-muted">
                                                            {new Date(notif.data_criacao).toLocaleDateString()}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </Dropdown.Item>
                                    );
                                })
                            )}
                            <Dropdown.Divider />
                            <Dropdown.Item
                                className="text-center small text-primary fw-bold"
                                onClick={() => navigate('/notifications-history')}
                            >
                                Ver histórico completo
                            </Dropdown.Item>
                        </Dropdown.Menu>
                    </Dropdown>

                    <Nav.Link href="#" className="me-3" style={{ color: '#94a3b8' }}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
                            <path d="M0 4a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V4zm2-1a1 1 0 0 0-1 1v.217l7 4.2 7-4.2V4a1 1 0 0 0-1-1H2zm13 2.383-4.708 2.825L15 11.105V5.383zm-.034 6.876-5.64-3.471L8 9.583l-1.326-.795-5.64 3.47A1 1 0 0 0 2 13h12a1 1 0 0 0 .966-.741zM1 11.105l4.708-2.897L1 5.383v5.722z" />
                        </svg>
                    </Nav.Link>

                    <Dropdown align="end">
                        <Dropdown.Toggle variant="transparent" id="dropdown-profile" className="d-flex align-items-center border-0 p-0">
                            <div className={styles.avatar}>
                                {user?.email?.charAt(0).toUpperCase() || 'U'}
                            </div>
                        </Dropdown.Toggle>

                        <Dropdown.Menu>
                            <Dropdown.Header>
                                <div className="fw-bold">{user?.email}</div>
                            </Dropdown.Header>
                            <Dropdown.Item href="#settings">Configurações</Dropdown.Item>
                            <Dropdown.Item href="#activity">Atividades</Dropdown.Item>
                            <Dropdown.Divider />
                            <Dropdown.Item onClick={logout} className="text-danger">Sair</Dropdown.Item>
                        </Dropdown.Menu>
                    </Dropdown>
                </Nav>
            </div>
        </Navbar>
    );
};

export default TopNavbar;
